import axios from "axios";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from "@stripe/react-stripe-js";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { fetchListingTaxSettings } from "../../Components/fetchListingTaxSettings";
import { calculateTaxes } from "../../Components/calculateTaxes";
import { DataContext } from "../../Context/Context";
import { handleDateAvailability } from "../../utilities/checkDateAvailability";
import DynamicSeoPage from "../DynamicSeoPage";
import { ArrowLeft } from "lucide-react";
import tabbyIcon from "../../Assets/tabby.png";
import CustomDropdown from "../../Components/CustomDropdown";
import { allCountryCodes } from "../../utilities/countryCodes";
import { Helmet } from "react-helmet";
import checkExtraGuests from "../../utilities/checkExtraGuest";

const Checkout = () => {
  const [msgtype, setMsgtype] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState("");
  const [tax, setTax] = useState([]);
  const { discount } = useContext(DataContext);
  const [, setIsAvailabilityChecking] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [totalPrices, setTotalPrices] = useState(0);
  const [calculatedPrices, setCalculatedPrices] = useState({
    markupPrice: 0,
    totalWithFees: 0,
  });
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+971",
  });

  const [weeklyDiscount, setWeeklyDiscount] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarData, setCalendarData] = useState({});
  const [selectedServices2, setSelectedServices2] = useState([]);
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const [services, setServices] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);

  const location = useLocation();
  const {
    formattedEndDate,
    formattedStartDate,
    countGuest,
    childCount,
    infantCount,
    bedLineFee,
    nights,
    updatedPrice,
    cleaningFee,
    additionalValue,
    selectedServices,
    listing,
  } = location.state || {};

  const [selectedPayment, setSelectedPayment] = useState("card");
  const { extraGuests, totalCharge } = checkExtraGuests(countGuest, nights);

  useEffect(() => {
    const fetchCalendarForListing = async () => {
      setCalendarLoading(true);
      try {
        // Fetch access token
        const tokenResponse = await axios.post(
          "https://365luxuryhomes.com/api/accessToken",
        );
        const accessToken = tokenResponse.data.access_token;
        // Fetch calendar data for the single listing
        const response = await axios.get(
          `https://api.hostaway.com/v1/listings/${listing?.id}/calendar`,
          {
            params: {
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              includeResources: 1,
            },
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Cache-Control": "no-cache",
            },
          },
        );
        setCalendarData(response.data.result);
      } catch (error) {
        // console.error("Error fetching calendar for the listing:", error);
      } finally {
        setCalendarLoading(false);
      }
    };
    if (listing.id && formattedStartDate && formattedEndDate) {
      fetchCalendarForListing();
    }
  }, [listing.id, formattedStartDate, formattedEndDate]);

  // Check if the selected dates are available
  const isDateAvailable = handleDateAvailability(
    calendarData,
    formattedStartDate,
    formattedEndDate,
  );

  // Fetch tax settings
  useEffect(() => {
    const fetchTaxSettings = async () => {
      try {
        const settings = await fetchListingTaxSettings(listing?.id);
        setTax(settings || []); // Ensure tax is an array
      } catch (error) {
        // console.error("Failed to fetch listing tax settings:", error.message);
      }
    };

    if (listing?.id) {
      fetchTaxSettings();
    }
  }, [listing?.id]);

  const { cityTourismTax, occupancyTax, vatGstTax } = calculateTaxes(
    tax,
    nights,
    updatedPrice,
  );

  const calculateWeeklyDiscount = (nights, price) => {
    if (nights >= 30) {
      return price * 0.2; // 20% monthly discount
    } else if (nights >= 7) {
      return price * 0.1; // 10% weekly discount
    }
    return 0;
  };

  // Calculate prices once when component mounts or dependencies change
  useEffect(() => {
    setPriceLoading(true);
    const calculatePrices = () => {
      const basePrice = updatedPrice || 0;
      const weeklyDiscountValue = calculateWeeklyDiscount(nights, basePrice);
      setWeeklyDiscount(weeklyDiscountValue);

      // Additional costs
      const addCosts =
        (additionalValue || 0) +
        (bedLineFee?.amount || 0) +
        (cleaningFee || 0) +
        (cityTourismTax || 0) +
        (occupancyTax || 0) +
        (totalCharge || 0) +
        (vatGstTax || 0);

      const appliedDiscount = weeklyDiscountValue || discount || 0;
      const discountPrice = Math.max(basePrice - appliedDiscount, 0);
      const markupPrice = discountPrice * 1.05;

      const markup = basePrice * 1.05;

      // Total with all fees
      const totalWithFees = Math.max(markupPrice + addCosts, 0);

      setCalculatedPrices({
        markupPrice: markup,
        totalWithFees,
      });

      setTotalPrices(totalWithFees);
    };

    calculatePrices();
  }, [
    updatedPrice,
    additionalValue,
    bedLineFee,
    cleaningFee,
    cityTourismTax,
    occupancyTax,
    vatGstTax,
    discount,
    weeklyDiscount,
    nights,
    totalCharge,
  ]);

  const addsOnServicesString = selectedServices
    ?.map((service) => `${service.label}: AED ${service.value}`)
    .join(", ");
  const additionalServicesString = selectedServices2
    ?.map((service) => `${service.label}`)
    .join(", ");

  const checkAvailability = async () => {
    setIsAvailabilityChecking(true);
    try {
      const tokenResponse = await axios.post(
        "https://365luxuryhomes.com/api/accessToken",
      );
      const accessToken = tokenResponse.data.access_token;

      const availabilityResponse = await axios.get(
        `https://api.hostaway.com/v1/listings/${listing?.id}/calendar`,
        {
          params: {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            includeResources: 1,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Cache-Control": "no-cache",
          },
        },
      );

      const dates = availabilityResponse?.data?.result || [];
      const isBooked = dates?.some((date) => date?.isBooked);

      if (isBooked) {
        throw new Error("Selected dates are no longer available");
      }

      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(errorMessage);
      return false;
    } finally {
      setIsAvailabilityChecking(false);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();

    setIsPaymentProcessing(true);
    setPaymentError(null);

    try {
      // Check availability first
      const isAvailable = await checkAvailability();
      if (!isAvailable) {
        setPaymentError("Selected dates are no longer available");
        return;
      }

      // Continue with existing payment logic
      const form = e.target;

      const fname = form.fname.value;
      const lname = form.lname.value;
      const phone = form.phone.value;
      const email = form.email.value;
      const message = form.message.value;

      const userData = {
        fname,
        lname,
        email,
        phone: formData.countryCode + phone,
        message,
      };

      const totalAfterCal = parseFloat(
        calculatedPrices?.totalWithFees?.toFixed(2),
      );

      const reservationData = {
        channelId: 2017,
        listingMapId: listing?.listingSettings?.listingMapId || 0,
        isManuallyChecked: 0,
        isInitial: 0,
        guestName: userData.fname + " " + userData.lname,
        guestFirstName: userData.fname,
        guestLastName: userData.lname,
        guestEmail: userData.email,
        guestPicture: userData?.picture || "",
        numberOfGuests: countGuest || 1,
        adults: countGuest || 1,
        children: childCount || 0,
        infants: infantCount || 0,
        pets: null,
        arrivalDate: formattedStartDate,
        departureDate: formattedEndDate,
        checkInTime: null,
        checkOutTime: null,
        phone: userData.phone,
        totalPrice: totalAfterCal,
        cleaningFee: cleaningFee || null,
        currency: "AED",
        comment: userData?.message || "",
        customFieldValues: [
          {
            customFieldId: 71097,
            value: addsOnServicesString,
          },
          {
            customFieldId: 70771,
            value: additionalServicesString,
          },
        ],
      };

      if (
        reservationData.guestName === undefined &&
        userData.guestEmail === undefined
      ) {
        toast.warn("Please provide your information");
        return;
      }

      if (!stripe || !elements) {
        setPaymentError("Stripe is not initialized yet!");
        return;
      }

      const { error, paymentMethod } = await stripe?.createPaymentMethod({
        type: "card",
        card: elements?.getElement(CardNumberElement),
      });

      if (error) {
        setPaymentError(error.message);
        return;
      }

      // Process payment with final calculated price
      const paymentResponse = await axios.post(
        "https://365luxuryhomes.com/api/stripe/payment",
        {
          paymentMethodId: paymentMethod.id,
          amount: calculatedPrices.totalWithFees,
          userEmail: form.email.value,
          reservationData: {
            ...reservationData,
            totalPrice: calculatedPrices.totalWithFees,
          },
        },
      );

      if (!paymentResponse.data.success) {
        throw new Error(paymentResponse.data.message || "Payment failed.");
      }
      setMsgtype("success");
      setMessage("Payment successful! Reservation confirmed.");
      toast.success("Payment successful! Reservation confirmed.");
      navigate("/payment-success");
      localStorage.removeItem("discount");
      return;
    } catch (error) {
      console.error("Error during payment or reservation:", error);
      if (error.response) {
        setMessage(error.response.data.message || "Something went wrong.");
        return;
      } else if (error.request) {
        setMessage("Network error. Please check your connection.");
        return;
      } else {
        setMessage(error.message || "An unexpected error occurred.");
      }
      setMsgtype("failed");
    } finally {
      setIsPaymentProcessing(false);
      return;
    }
  };

  // useEffect(() => {
  //   if (listing?.id) {
  //     // Calculate base price and all fees
  //     const basePrice = updatedPrice;
  //     const additionalCosts =
  //       additionalValue +
  //       bedLineFee?.amount +
  //       cleaningFee +
  //       cityTourismTax +
  //       occupancyTax +
  //       vatGstTax;

  //     // Apply markup and discounts
  //     const priceWithMarkup = basePrice * 1.05;
  //     let finalPrice;

  //     if (discount && discount > 0) {
  //       finalPrice = priceWithMarkup - discount + additionalCosts;
  //     } else {
  //       finalPrice = priceWithMarkup + additionalCosts;
  //     }

  //     setTotalPrices(finalPrice);
  //     setPriceLoading(false);
  //   }
  // }, [
  //   listing,
  //   discount,
  //   updatedPrice,
  //   additionalValue,
  //   bedLineFee,
  //   cleaningFee,
  //   cityTourismTax,
  //   occupancyTax,
  //   vatGstTax,
  // ]);

  useEffect(() => {
    if (listing?.id) {
      // 1️⃣ Calculate base price and all fees
      const basePrice = updatedPrice || 0;
      const additionalCosts =
        (additionalValue || 0) +
        (bedLineFee?.amount || 0) +
        (cleaningFee || 0) +
        (cityTourismTax || 0) +
        (occupancyTax || 0) +
        (vatGstTax || 0);

      // 2️⃣ Apply discount on base price first
      const discountedPrice =
        discount && discount > 0 ? basePrice - discount : basePrice;

      // 3️⃣ Apply 5% markup after discount
      const priceWithMarkup = discountedPrice * 1.05;

      // 4️⃣ Add all additional costs and taxes
      const finalPrice = priceWithMarkup + additionalCosts;

      // 5️⃣ Update state
      setTotalPrices(finalPrice);
      setPriceLoading(false);
    }
  }, [
    listing?.id,
    updatedPrice,
    additionalValue,
    bedLineFee,
    cleaningFee,
    cityTourismTax,
    occupancyTax,
    vatGstTax,
    discount,
  ]);

  const generateTitle = () => {
    if (nights >= 7 && nights < 30) {
      return "Weekly Discount";
    } else if (nights >= 30) {
      return "Monthly Discount";
    }
  };

  const handleTabbyCheckout = async (e) => {
    e.preventDefault();

    // Check availability first
    const isAvailable = await checkAvailability();
    if (!isAvailable) {
      setPaymentError("Selected dates are no longer available");
      return;
    }

    const form = e.target;

    const userData = {
      fname: form.fname.value,
      lname: form.lname.value,
      phone: formData.countryCode + form.phone.value,
      email: form.email.value,
      message: form.message.value,
    };

    const reservationPayload = {
      channelId: 2017,
      listingMapId: listing?.listingSettings?.listingMapId || 0,
      isManuallyChecked: 0,
      isInitial: 0,
      guestName: `${userData.fname} ${userData.lname}`,
      guestFirstName: userData.fname,
      guestLastName: userData.lname,
      guestEmail: userData.email,
      guestPicture: userData?.picture || "",
      numberOfGuests: countGuest || 1,
      adults: countGuest || 1,
      children: childCount || 0,
      infants: infantCount || 0,
      pets: null,
      arrivalDate: formattedStartDate,
      departureDate: formattedEndDate,
      checkInTime: null,
      checkOutTime: null,
      phone: userData.phone,
      totalPrice: totalPrices,
      cleaningFee: cleaningFee || null,
      currency: "AED",
      comment: userData?.comment || "",
    };

    try {
      const response = await axios.post(
        "https://365luxuryhomes.com/api/create-tabby-session",
        {
          guestFirstName: reservationPayload.guestFirstName,
          guestLastName: reservationPayload.guestLastName,
          guestEmail: reservationPayload.guestEmail,
          phone: reservationPayload.phone,
          arrivalDate: reservationPayload.arrivalDate,
          departureDate: reservationPayload.departureDate,
          totalPrice: reservationPayload.totalPrice,
          currency: reservationPayload.currency,
          listingMapId: reservationPayload.listingMapId,
          reservationData: reservationPayload,
        },
        {
          withCredentials: true,
        },
      );

      const checkoutOptions = response.data.checkout_url;

      if (
        checkoutOptions &&
        checkoutOptions.length > 0 &&
        checkoutOptions[0].web_url
      ) {
        // Redirect the user to the Tabby checkout page.
        window.location.href = checkoutOptions[0].web_url;
      } else {
        console.error(
          "Tabby redirect URL (web_url) not found in the response.",
          checkoutOptions,
        );
        alert(
          "Sorry, Tabby payment is currently not available for this order.",
        );
      }
    } catch (error) {
      console.error("Payment failed:", error);
      alert(
        "Payment failed: " + (error.response?.data?.error || error.message),
      );
    }
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(
          "https://365luxuryhomes.com/api/services",
        );
        setServices(response.data);
      } catch (error) {
        // console.error("Error fetching calendar for the listing:", error);
      } finally {
      }
    };
    fetchServices();
  }, []);

  const handleServiceChange = (e, service) => {
    const { checked } = e.target;
    setSelectedServices2((prev) => {
      if (checked) {
        return [...prev, service];
      } else {
        return prev.filter((item) => item._id !== service._id);
      }
    });
  };

  return (
    <>
      <Helmet>
        <title>Checkout | 365 Luxury Homes</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <section className="lg:py-28 md:py-20 py-16 md:pt-0 pt-24">
        <DynamicSeoPage
          page={"checkout"}
          defalut={"Checkout - 365 Luxury Homes"}
        />

        <div className="container">
          <div className="flex items-center gap-4 mb-5">
            <span
              onClick={() => navigate(-1)}
              className="p-2 bg-[#e6e6e6] rounded-full block w-10 h-10 text-[#c3a963] hover:bg-[#c3a963] hover:text-white cursor-pointer"
            >
              <ArrowLeft size={24} className="" />
            </span>
            <h1 className="text-2xl font-bold text-[#74716d]">
              Confirm and pay
            </h1>
          </div>
          <form
            // onSubmit={handleCheckout}
            onSubmit={
              selectedPayment === "tabby" ? handleTabbyCheckout : handleCheckout
            }
            className="lg:p-8 md:p-6 p-4 bg-gray-50"
            autoComplete="off"
          >
            <div className="grid lg:grid-cols-2 gap-5 mb-6">
              <div className="">
                {!isDateAvailable && !calendarLoading && (
                  <p className="text-red-500 text-md mb-2">
                    Sorry, the check-in date you selected is no longer
                    available. Please choose a different date to continue your
                    booking. <Link to={`/listing/${ref}`}>here</Link>
                  </p>
                )}
                <table className="w-full border-collapse bg-white shadow rounded-lg">
                  <thead>
                    <tr className="">
                      <th className="text-left border-b p-2 text-[#74716d] text-[14px]">
                        Details
                      </th>
                      <th className="text-right border-b p-2 text-[#74716d] text-[14px]">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">Guests</td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {countGuest || 0} Adults, {childCount || 0} Children,{" "}
                        {infantCount || 0} Infants
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        Start Date
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {formattedStartDate}
                      </td>
                    </tr>

                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        End Date
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {formattedEndDate}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        Total Nights
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {priceLoading ? (
                          <div className="skeleton h-4 w-20 ml-auto"></div>
                        ) : (
                          <>{nights} Nights</>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        Base Price
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {priceLoading ? (
                          <div className="skeleton h-4 w-32 ml-auto"></div>
                        ) : (
                          <>
                            AED{" "}
                            {calculatedPrices?.markupPrice?.toFixed(2) ||
                              "0.00"}
                          </>
                        )}
                      </td>
                    </tr>
                    {weeklyDiscount > 0 && (
                      <tr>
                        <td className="p-2 text-red-500 text-[14px]">
                          {generateTitle()}
                        </td>
                        <td className="p-2 text-right text-red-500 text-[14px]">
                          {priceLoading ? (
                            <div className="skeleton h-4 w-24 ml-auto"></div>
                          ) : (
                            <>- AED {weeklyDiscount?.toFixed(2) || "0.00"}</>
                          )}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        Stay fee
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {priceLoading ? (
                          <div className="skeleton h-4 w-28 ml-auto"></div>
                        ) : (
                          <>
                            AED{" "}
                            {(
                              cleaningFee +
                              bedLineFee.amount +
                              occupancyTax
                            ).toFixed(2) || "0.00"}
                          </>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        Add-ons services
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {priceLoading ? (
                          <div className="skeleton h-4 w-28 ml-auto"></div>
                        ) : (
                          <>AED {additionalValue}</>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        City / Tourism tax
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {priceLoading ? (
                          <div className="skeleton h-4 w-28 ml-auto"></div>
                        ) : (
                          <>AED {(cityTourismTax || 0)?.toFixed(2) || "0.00"}</>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 text-[#74716d] text-[14px]">
                        VAT / GST 5 % (per reservation)
                      </td>
                      <td className="p-2 text-right text-[#74716d] text-[14px]">
                        {priceLoading ? (
                          <div className="skeleton h-4 w-20 ml-auto"></div>
                        ) : (
                          <>AED {(vatGstTax || 0)?.toFixed(2) || "0.00"}</>
                        )}
                      </td>
                    </tr>
                    {discount && discount > 0 && (
                      <tr className="border-t-[1px]">
                        <td className="p-2 text-[#74716d] text-[16px]">
                          Coupon Applied
                        </td>
                        <td className="p-2 text-right text-[#74716d] text-[16px]">
                          {priceLoading ? (
                            <div className="skeleton h-4 w-24 ml-auto"></div>
                          ) : (
                            <>- AED {(discount || 0)?.toFixed(2) || "0.00"}</>
                          )}
                        </td>
                      </tr>
                    )}
                    {extraGuests && (
                      <tr>
                        <td className="p-2 text-[#74716d] text-[14px]">
                          Extra Guest {extraGuests}
                        </td>
                        <td className="p-2 text-right text-[#74716d] text-[14px]">
                          {priceLoading ? (
                            <div className="skeleton h-4 w-20 ml-auto"></div>
                          ) : (
                            <>AED {(totalCharge || 0)?.toFixed(2) || "0.00"}</>
                          )}
                        </td>
                      </tr>
                    )}
                    <tr className="border-t-[1px]">
                      <td className="p-2 text-[#74716d] text-[16px] font-semibold">
                        Subtotal
                      </td>

                      <td className="p-2 text-right text-[#74716d] text-[16px] font-semibold">
                        {priceLoading ? (
                          <div className="skeleton h-4 w-36 ml-auto"></div>
                        ) : (
                          <>
                            AED{" "}
                            {calculatedPrices?.totalWithFees?.toFixed(2) ||
                              "0.00"}
                          </>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-white p-6 shadow rounded-lg">
                <h3 className="text-[1.2rem] leading-[1rem] font-semibold text-[#74716d] mb-4">
                  Additional information
                </h3>
                <textarea
                  placeholder="Tell us something about your booking and stay..."
                  name="message"
                  className="border p-2 rounded w-full text-[#74716d] text-[14px] focus:ring-[#aca9a5] focus:border-[#aca9a5] md:h-full max-h-[20rem]"
                ></textarea>
              </div>
            </div>

            <div className="bg-white p-6 shadow rounded-lg mb-6">
              <h3 className="text-[1.2rem] leading-[1rem] font-semibold text-[#74716d] mb-4">
                Additional Services
              </h3>
              <div className="grid lg:grid-cols-4 gap-5">
                {services
                  ?.filter((service) => service?.category === "Additional")
                  ?.map((service) => (
                    <label
                      key={service?._id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        value={service?.value}
                        checked={selectedServices2.some(
                          (item) => item?._id === service?._id,
                        )}
                        onChange={(e) => handleServiceChange(e, service)}
                        className="w-4 h-4 text-[#74716d] focus:ring-[#aca9a5] border-gray-300 rounded"
                      />
                      <span className="text-[#74716d] text-[16px]">
                        {service?.label}
                      </span>
                    </label>
                  ))}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              <div className="bg-white p-6 shadow rounded-lg">
                <h2 className="text-[1.2rem] leading-[1rem] font-semibold mb-4 text-[#74716d]">
                  Billing details
                </h2>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="First name"
                    name="fname"
                    className="border-b p-2 w-full text-[#74716d] focus:ring-[#aca9a5] focus:border-[#aca9a5] text-[14px] pb-12 focus:outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last name"
                    name="lname"
                    className="border-b p-2 w-full text-[#74716d] focus:ring-[#aca9a5] focus:border-[#aca9a5] text-[14px] pb-12 focus:outline-none"
                    required
                  />
                  <div className="flex">
                    <div className="w-60">
                      <CustomDropdown
                        allCountryCodes={allCountryCodes}
                        formData={formData}
                        setFormData={setFormData}
                        fieldHeight="100px"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Phone"
                      name="phone"
                      className="border-b p-2 w-full text-[#74716d] focus:ring-[#aca9a5] focus:border-[#aca9a5] text-[14px] pb-4 focus:outline-none"
                      required
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="Email address"
                    name="email"
                    className="border-b p-2 w-full text-[#74716d] focus:ring-[#aca9a5] focus:border-[#aca9a5] text-[14px] pb-12 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="bg-white p-6 shadow rounded-lg">
                <div className="bg-gray-100 flex items-center justify-center">
                  <div className="bg-white  max-w-lg w-full mx-auto">
                    {/* --- Title --- */}
                    <h2 className="text-[1.2rem] leading-[1rem] font-semibold text-[#74716d] mb-6">
                      Payment Information
                    </h2>

                    {/* --- Payment Method Selection --- */}
                    <div className="space-y-4 mb-6">
                      {/* Credit Card Option */}
                      <div
                        onClick={() => setSelectedPayment("card")}
                        className={`cursor-pointer border rounded-lg p-4 transition-all duration-200 ${
                          selectedPayment === "card"
                            ? "border-blue-500 ring-2 ring-blue-200 "
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name="payment_method"
                            id="card_payment"
                            checked={selectedPayment === "card"}
                            onChange={() => setSelectedPayment("card")}
                            className="radio radio-neutral h-4 w-4 rounded-full text-teal-600 focus:ring-teal-500 border-gray-300"
                          />
                          <label
                            htmlFor="card_payment"
                            className="ml-3 block text-sm font-medium text-gray-700"
                          >
                            Pay with Card
                          </label>
                        </div>
                        {selectedPayment === "card" && (
                          <div className="mt-4 animate-fade-in">
                            <div className="space-y-4 border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500">
                              <CardNumberElement className="border p-2 rounded-md w-full text-[#74716d] text-[14px] focus:ring-[#6c675b] focus:border-yellow-500 py-4" />
                              <CardExpiryElement className="border p-2 rounded-md w-full text-[#74716d] text-[14px] focus:ring-[#6c675b] focus:border-yellow-500 py-4" />
                              <CardCvcElement className="border p-2 rounded-md w-full text-[#74716d] text-[14px] focus:ring-[#6c675b] focus:border-yellow-500 py-4" />
                            </div>
                          </div>
                        )}
                        {/* <div className="mt-4 animate-fade-in">
                          <div className="space-y-4 border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500">
                            <CardNumberElement className="border p-2 rounded-md w-full text-[#74716d] text-[14px] focus:ring-[#6c675b] focus:border-yellow-500 py-4" />
                            <CardExpiryElement className="border p-2 rounded-md w-full text-[#74716d] text-[14px] focus:ring-[#6c675b] focus:border-yellow-500 py-4" />
                            <CardCvcElement className="border p-2 rounded-md w-full text-[#74716d] text-[14px] focus:ring-[#6c675b] focus:border-yellow-500 py-4" />
                          </div>
                        </div> */}
                      </div>

                      {/* Tabby Option */}
                      <div
                        onClick={() => setSelectedPayment("tabby")}
                        className={`cursor-pointer border rounded-lg p-4 transition-all duration-200 ${
                          selectedPayment === "tabby"
                            ? "border-teal-500 ring-2 ring-teal-200 bg-teal-50"
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name="payment_method"
                            id="tabby_payment"
                            checked={selectedPayment === "tabby"}
                            onChange={() => setSelectedPayment("tabby")}
                            className="radio radio-accent h-4 w-4 rounded-full text-teal-600 focus:ring-teal-500 border-gray-300"
                          />
                          <label
                            htmlFor="tabby_payment"
                            className="ml-3 flex items-center space-x-3"
                          >
                            <img src={tabbyIcon} alt="" className="w-20" />
                            <span className="block  text-gray-700">
                              Pay later with Tabby.
                            </span>
                          </label>
                        </div>
                        <p className="ml-8 mt-2  text-gray-600">
                          Use any card.
                        </p>
                      </div>
                    </div>

                    {/* --- Action Button --- */}
                    {/* <button
                    onClick={
                      selectedPayment === "card"
                        ? handleCheckout
                        : handleTabbyCheckout
                    }
                    className="w-full bg-[#C2B280] text-white font-bold py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C2B280]"
                  >
                    {selectedPayment === "card"
                      ? "Confirm Booking"
                      : "Continue with Tabby"}
                  </button> */}
                    <button
                      type="submit"
                      className="w-full bg-[#C2B280] text-white font-bold py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C2B280]"
                    >
                      {selectedPayment === "card"
                        ? "Confirm Booking"
                        : "Continue with Tabby"}
                    </button>
                  </div>
                </div>
                <div className="space-y-4">
                  {message && (
                    <p
                      className={`${
                        msgtype === "failed" ? "text-red-500" : "text-green-500"
                      } text-[14px] mt-4`}
                    >
                      {message}
                    </p>
                  )}
                  {paymentError && (
                    <div className="text-red-500 text-sm mt-2">
                      {paymentError}
                    </div>
                  )}
                  {isPaymentProcessing && (
                    <div className="text-center py-4">
                      <span className="loading loading-spinner loading-lg"></span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default Checkout;
